import type { JSX } from '@builder.io/qwik';
import { component$ } from '@builder.io/qwik';
import ArticleBuilder from '~/components/Layout/Builders/ArticleBuilder';
import Body from '~/components/Layout/Body/Body';
import Container from '~/components/Layout/Body/Container';
import Disqus from '~/components/Shared/Embeds/Disqus';
import VideoPlayer from '~/components/Shared/Video/VideoPlayer';
import ArticleAd from '~/components/Shared/Ads/ArticleAd';
import Article from '~/components/Layout/Body/Article/Article';
import ArticleAuthorBio from '~/components/Layout/Body/Article/ArticleBody/ArticleAuthorBio';
import ArticleBottom from '~/components/Layout/Body/Article/ArticleBottom';
import ArticleByline from '~/components/Layout/Body/Article/ArticleHeader/ArticleByline';
import ArticleHeader from '~/components/Layout/Body/Article/ArticleHeader/ArticleHeader';
import ArticleInThisStory from '~/components/Layout/Body/Article/ArticleHeader/ArticleInThisStory';
import ArticleIntro from '~/components/Layout/Body/Article/ArticleHeader/ArticleIntro';
import ArticleTitle from '~/components/Layout/Body/Article/ArticleHeader/ArticleTitle';
import ArticleBody from '~/components/Layout/Body/Article/ArticleBody/ArticleBody';
import ArticleBreadcrumbs from '~/components/Layout/Body/Article/ArticleBody/ArticleBreadcrumbs';
import ArticleContent from '~/components/Layout/Body/Article/ArticleContent';
import ArticleCover from '~/components/Layout/Body/Article/ArticleCover/ArticleCover';
import ArticleCoverImage from '~/components/Layout/Body/Article/ArticleCover/ArticleCoverImage';
import ArticleCoverVideo from '~/components/Layout/Body/Article/ArticleCover/ArticleCoverVideo';
import ArticleDisclaimer from '~/components/Layout/Body/Article/ArticleBody/ArticleDisclaimer';
import ArticleMain from '~/components/Layout/Body/Article/ArticleBody/ArticleMain';
import ArticleMainCategory from '../Body/Article/ArticleHeader/ArticleMainCategory';
import ArticlePrimary from '~/components/Layout/Body/Article/ArticleBody/ArticlePrimary';
import ArticleRelatedPosts from '~/components/Layout/Body/Article/ArticleBody/ArticleRelatedPosts';
import ArticleRelatedTopics from '~/components/Layout/Body/Article/ArticleBody/ArticleRelatedTopics';
import ArticleShare from '~/components/Layout/Body/Article/ArticleShare/ArticleShare';
import ArticleSidebar from '~/components/Layout/Body/Article/ArticleBody/ArticleSidebar';
import ArticleSponsor from '~/components/Layout/Body/Article/ArticleHeader/ArticleSponsor';
import BottomPlaceholder from '~/components/Shared/Ads/BottomPlaceholder';
import DividerBlock from '~/components/Layout/Body/Blocks/DividerBlock/DividerBlock';
import KyivHeader from '../Body/Article/ArticleHeader/KyivHeader';
import KyivAuthorBio from '../Body/Article/ArticleBody/KyivAuthorBio';
import PremiumArticle from '../Body/Article/ArticleBody/PremiumArticle';
import PremiumHeader from '../Body/Article/ArticleHeader/PremiumHeader';
import RelatedArticles from '../Body/Article/ArticleBody/RelatedArticles';
import SlideShow from '~/components/Shared/SlideShow/SlideShow';
import SlideShowNext from '~/components/Shared/SlideShow/SlideShowNext';
import Sportradar from '~/components/Shared/Widgets/Sportradar';
import VoltaxRecirculationWidget from '~/components/Layout/Body/Article/ArticleBody/VoltaxRecirculationWidget';
import type { ArticleItemType } from '~/types/ArticleItemType';
import { Logger } from '~/utilities/logger';
import { setStoreItem } from '~/utilities/Store';
import { LAST_LOADED } from '~/constants/LAST_LOADED';

const componentMap: Record<string, any> = {
  Article,
  ArticleAd,
  ArticleAuthorBio,
  ArticleBody,
  ArticleBottom,
  ArticleBreadcrumbs,
  ArticleByline,
  ArticleContent,
  ArticleCover,
  ArticleCoverImage,
  ArticleCoverVideo,
  ArticleDisclaimer,
  ArticleHeader,
  ArticleInThisStory,
  ArticleIntro,
  ArticleMain,
  ArticleMainCategory,
  ArticlePrimary,

  // ArticleRelatedPosts uses relatedPosts and shows posts in a grid
  ArticleRelatedPosts,
  ArticleRelatedTopics,
  ArticleShare,
  ArticleSidebar,
  ArticleSponsor,
  ArticleTitle,
  Body,
  BottomPlaceholder,
  Container,
  Disqus,
  DividerBlock,
  KyivHeader,
  KyivAuthorBio,
  PremiumArticle,
  PremiumHeader,

  // RelatedArticles uses additionalArticles and has a main article followed by 3 smaller articles
  RelatedArticles,
  SlideShow,
  SlideShowNext,
  Sportradar,
  VideoPlayer,
  VoltaxRecirculationWidget,
};

type SingleArticleItemType = {
  item: ArticleItemType;
  experiments?: string[];
};

export default component$<SingleArticleItemType>(
  ({ item, experiments }): JSX.Element => {
    const Component = componentMap[item.component] || null;
    setStoreItem(LAST_LOADED, item.component);

    if (!Component) {
      new Logger().error('Article component not found: ' + item.component);
      return <></>;
    }

    return item.children ? (
      <Component id={item.id} {...item.options}>
        <ArticleBuilder items={item.children} experiments={experiments} />
      </Component>
    ) : (
      <Component id={item.id} {...item.options} />
    );
  }
);
